import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { useModelStore } from "../state/store";
import { subscribeViewportCommands, type ViewportCommand } from "./commands";

const DEG = 180 / Math.PI;

function disposeGroup(group: THREE.Group): void {
  group.traverse((object) => {
    if (object instanceof THREE.Mesh || object instanceof THREE.LineSegments) {
      object.geometry.dispose();
      if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
      else object.material.dispose();
    }
  });
  group.clear();
}

function addExpression(expression: string, delta: number): string {
  if (Math.abs(delta) < 1e-6) return expression;
  return `(${expression}) ${delta >= 0 ? "+" : "-"} ${Math.abs(delta).toFixed(3)}`;
}

export function Viewport(): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const evaluation = useModelStore((state) => state.evaluation);
  const selectedNodeIds = useModelStore((state) => state.selectedNodeIds);
  const transformMode = useModelStore((state) => state.transformMode);
  const projection = useModelStore((state) => state.document.viewport.projection);
  const showGrid = useModelStore((state) => state.document.viewport.showGrid);
  const showEdges = useModelStore((state) => state.document.viewport.showEdges);
  const background = useModelStore((state) => state.document.viewport.background);

  const runtimeRef = useRef<{
    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    perspective: THREE.PerspectiveCamera;
    orthographic: THREE.OrthographicCamera;
    camera: THREE.Camera;
    controls: OrbitControls;
    transform: TransformControls;
    bodyRoot: THREE.Group;
    grid: THREE.GridHelper;
    raycaster: THREE.Raycaster;
    pointer: THREE.Vector2;
    hovered: THREE.Mesh | null;
    selectedGroup: THREE.Group | null;
    invalidate: () => void;
    fit: () => void;
    setProjection: (projection: "perspective" | "orthographic") => void;
  } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(background);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: false });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    container.appendChild(renderer.domElement);

    const perspective = new THREE.PerspectiveCamera(42, 1, 0.1, 100000);
    perspective.position.set(120, -150, 110);
    perspective.up.set(0, 0, 1);
    const orthographic = new THREE.OrthographicCamera(-100, 100, 100, -100, -10000, 10000);
    orthographic.position.copy(perspective.position);
    orthographic.up.set(0, 0, 1);
    let activeCamera: THREE.Camera = projection === "orthographic" ? orthographic : perspective;

    const controls = new OrbitControls(activeCamera, renderer.domElement);
    controls.target.set(0, 0, 15);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.screenSpacePanning = true;
    controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
    controls.mouseButtons.MIDDLE = THREE.MOUSE.PAN;
    controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;

    const ambient = new THREE.HemisphereLight(0xffffff, 0x222936, 2.4);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 3.2);
    key.position.set(150, -100, 220);
    key.castShadow = true;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x9bc8ff, 1.2);
    fill.position.set(-150, 80, 100);
    scene.add(fill);

    const grid = new THREE.GridHelper(1000, 100, 0x4a515e, 0x2a2f38);
    grid.rotation.x = Math.PI / 2;
    grid.position.z = -0.01;
    scene.add(grid);
    const axes = new THREE.AxesHelper(40);
    scene.add(axes);

    const bodyRoot = new THREE.Group();
    scene.add(bodyRoot);

    const transform = new TransformControls(activeCamera, renderer.domElement);
    transform.setSpace("world");
    transform.setSize(0.8);
    scene.add(transform.getHelper());

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered: THREE.Mesh | null = null;
    let framePending = false;
    let disposed = false;

    const render = (): void => {
      framePending = false;
      if (disposed) return;
      controls.update();
      renderer.render(scene, activeCamera);
    };
    const invalidate = (): void => {
      if (framePending || disposed) return;
      framePending = true;
      requestAnimationFrame(render);
    };

    controls.addEventListener("change", invalidate);
    transform.addEventListener("change", invalidate);
    transform.addEventListener("dragging-changed", (event) => {
      controls.enabled = !event.value;
    });

    const fit = (): void => {
      const box = new THREE.Box3().setFromObject(bodyRoot);
      if (box.isEmpty()) {
        controls.target.set(0, 0, 0);
        activeCamera.position.set(120, -150, 110);
        invalidate();
        return;
      }
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxSize = Math.max(size.x, size.y, size.z, 1);
      const direction = activeCamera.position.clone().sub(controls.target).normalize();
      controls.target.copy(center);
      if (activeCamera instanceof THREE.PerspectiveCamera) {
        const distance = maxSize / (2 * Math.tan((activeCamera.fov * Math.PI) / 360));
        activeCamera.position.copy(center).add(direction.multiplyScalar(distance * 1.7));
        activeCamera.near = Math.max(distance / 1000, 0.01);
        activeCamera.far = distance * 100;
        activeCamera.updateProjectionMatrix();
      } else if (activeCamera instanceof THREE.OrthographicCamera) {
        const aspect = Math.max(container.clientWidth / Math.max(container.clientHeight, 1), 0.1);
        activeCamera.left = (-maxSize * aspect) / 1.4;
        activeCamera.right = (maxSize * aspect) / 1.4;
        activeCamera.top = maxSize / 1.4;
        activeCamera.bottom = -maxSize / 1.4;
        activeCamera.position.copy(center).add(direction.multiplyScalar(maxSize * 2.5));
        activeCamera.updateProjectionMatrix();
      }
      controls.update();
      invalidate();
    };

    const setProjection = (nextProjection: "perspective" | "orthographic"): void => {
      const next = nextProjection === "orthographic" ? orthographic : perspective;
      if (activeCamera === next) return;
      next.position.copy(activeCamera.position);
      next.quaternion.copy(activeCamera.quaternion);
      activeCamera = next;
      controls.object = next;
      transform.camera = next;
      if (runtimeRef.current) runtimeRef.current.camera = next;
      fit();
    };

    const setView = (command: ViewportCommand): void => {
      const box = new THREE.Box3().setFromObject(bodyRoot);
      const center = box.isEmpty() ? new THREE.Vector3() : box.getCenter(new THREE.Vector3());
      const size = box.isEmpty() ? 100 : Math.max(...box.getSize(new THREE.Vector3()).toArray(), 1);
      const distance = size * 2.5;
      const directionByCommand: Record<Exclude<ViewportCommand, "fit">, THREE.Vector3> = {
        iso: new THREE.Vector3(1, -1, 0.8),
        top: new THREE.Vector3(0, 0, 1),
        bottom: new THREE.Vector3(0, 0, -1),
        front: new THREE.Vector3(0, -1, 0),
        back: new THREE.Vector3(0, 1, 0),
        right: new THREE.Vector3(1, 0, 0),
        left: new THREE.Vector3(-1, 0, 0)
      };
      if (command === "fit") {
        fit();
        return;
      }
      const direction = directionByCommand[command].normalize();
      activeCamera.position.copy(center).add(direction.multiplyScalar(distance));
      activeCamera.up.set(0, 0, Math.abs(direction.z) > 0.95 ? 0 : 1);
      if (Math.abs(direction.z) > 0.95) activeCamera.up.set(0, 1, 0);
      controls.target.copy(center);
      activeCamera.lookAt(center);
      controls.update();
      fit();
    };

    const resize = (): void => {
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      renderer.setSize(width, height, false);
      perspective.aspect = width / height;
      perspective.updateProjectionMatrix();
      const scale = Math.max(orthographic.top - orthographic.bottom, 1);
      orthographic.left = (-scale * width) / height / 2;
      orthographic.right = (scale * width) / height / 2;
      orthographic.updateProjectionMatrix();
      invalidate();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    const pointerFromEvent = (event: PointerEvent): void => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const setHover = (mesh: THREE.Mesh | null): void => {
      if (hovered === mesh) return;
      if (hovered?.material instanceof THREE.MeshStandardMaterial) hovered.material.emissiveIntensity = hovered.userData.selected ? 0.25 : 0;
      hovered = mesh;
      if (hovered?.material instanceof THREE.MeshStandardMaterial) hovered.material.emissiveIntensity = 0.45;
      renderer.domElement.style.cursor = hovered ? "pointer" : "default";
      invalidate();
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (transform.dragging) return;
      pointerFromEvent(event);
      raycaster.setFromCamera(pointer, activeCamera);
      const intersections = raycaster.intersectObjects(bodyRoot.children, true);
      const mesh = intersections.find((hit) => hit.object instanceof THREE.Mesh)?.object;
      setHover(mesh instanceof THREE.Mesh ? mesh : null);
    };

    const onPointerDown = (event: PointerEvent): void => {
      if (transform.dragging) return;
      pointerFromEvent(event);
      raycaster.setFromCamera(pointer, activeCamera);
      const intersections = raycaster.intersectObjects(bodyRoot.children, true);
      const hit = intersections.find((entry) => entry.object instanceof THREE.Mesh)?.object;
      if (hit instanceof THREE.Mesh) {
        const nodeId = hit.userData.nodeId as string | undefined;
        if (nodeId) useModelStore.getState().selectNode(nodeId, event.ctrlKey || event.metaKey || event.shiftKey);
      } else if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
        useModelStore.getState().clearSelection();
      }
    };

    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerdown", onPointerDown);

    const onTransformEnd = (): void => {
      const object = transform.object;
      if (!(object instanceof THREE.Group)) return;
      const nodeId = object.userData.nodeId as string | undefined;
      if (!nodeId) return;
      const position = object.position.clone();
      const rotation = new THREE.Vector3(object.rotation.x * DEG, object.rotation.y * DEG, object.rotation.z * DEG);
      const scale = (object.scale.x + object.scale.y + object.scale.z) / 3;
      object.position.set(0, 0, 0);
      object.rotation.set(0, 0, 0);
      object.scale.set(1, 1, 1);

      const store = useModelStore.getState();
      const node = store.document.nodes.find((candidate) => candidate.id === nodeId);
      if (!node) return;
      if (store.transformMode === "translate") {
        if ("placement" in node) {
          store.updateNode(nodeId, (draft) => {
            if ("placement" in draft) {
              draft.placement.position = [
                addExpression(draft.placement.position[0], position.x),
                addExpression(draft.placement.position[1], position.y),
                addExpression(draft.placement.position[2], position.z)
              ];
            }
          }, "Move feature");
        } else if (node.type === "transform") {
          store.updateNode(nodeId, (draft) => {
            if (draft.type === "transform") {
              draft.translation = [
                addExpression(draft.translation[0], position.x),
                addExpression(draft.translation[1], position.y),
                addExpression(draft.translation[2], position.z)
              ];
            }
          }, "Move feature");
        } else {
          store.addOperation("transform");
          const transformId = useModelStore.getState().selectedNodeIds[0];
          if (transformId) store.updateNode(transformId, (draft) => {
            if (draft.type === "transform") draft.translation = [position.x.toFixed(3), position.y.toFixed(3), position.z.toFixed(3)];
          }, "Move feature");
        }
      } else if (store.transformMode === "rotate") {
        if ("placement" in node) {
          store.updateNode(nodeId, (draft) => {
            if ("placement" in draft) {
              draft.placement.rotation = [
                addExpression(draft.placement.rotation[0], rotation.x),
                addExpression(draft.placement.rotation[1], rotation.y),
                addExpression(draft.placement.rotation[2], rotation.z)
              ];
            }
          }, "Rotate feature");
        } else {
          if (node.type !== "transform") store.addOperation("transform");
          const transformId = node.type === "transform" ? nodeId : useModelStore.getState().selectedNodeIds[0];
          if (transformId) store.updateNode(transformId, (draft) => {
            if (draft.type === "transform") draft.rotation = [rotation.x.toFixed(3), rotation.y.toFixed(3), rotation.z.toFixed(3)];
          }, "Rotate feature");
        }
      } else if (store.transformMode === "scale" && Math.abs(scale - 1) > 1e-4) {
        if (node.type !== "transform") store.addOperation("transform");
        const transformId = node.type === "transform" ? nodeId : useModelStore.getState().selectedNodeIds[0];
        if (transformId) store.updateNode(transformId, (draft) => {
          if (draft.type === "transform") draft.scale = node.type === "transform" ? `(${draft.scale}) * ${scale.toFixed(4)}` : scale.toFixed(4);
        }, "Scale feature");
      }
      invalidate();
    };
    transform.addEventListener("mouseUp", onTransformEnd);

    const unsubscribeCommands = subscribeViewportCommands(setView);

    runtimeRef.current = {
      scene,
      renderer,
      perspective,
      orthographic,
      camera: activeCamera,
      controls,
      transform,
      bodyRoot,
      grid,
      raycaster,
      pointer,
      hovered,
      selectedGroup: null,
      invalidate,
      fit,
      setProjection
    };
    invalidate();

    return () => {
      disposed = true;
      unsubscribeCommands();
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      transform.removeEventListener("mouseUp", onTransformEnd);
      controls.dispose();
      transform.dispose();
      disposeGroup(bodyRoot);
      renderer.dispose();
      renderer.domElement.remove();
      runtimeRef.current = null;
    };
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.scene.background = new THREE.Color(background);
    runtime.grid.visible = showGrid;
    runtime.invalidate();
  }, [background, showGrid]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.setProjection(projection);
  }, [projection]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.transform.detach();
    disposeGroup(runtime.bodyRoot);
    if (!evaluation) {
      runtime.invalidate();
      return;
    }

    for (const body of evaluation.bodies) {
      const group = new THREE.Group();
      group.userData.nodeId = body.nodeId;
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(body.vertices, 3));
      geometry.setAttribute("normal", new THREE.BufferAttribute(body.normals, 3));
      geometry.setIndex(new THREE.BufferAttribute(body.triangles, 1));
      geometry.computeBoundingSphere();
      const selected = selectedNodeIds.includes(body.nodeId);
      const material = new THREE.MeshStandardMaterial({
        color: body.color,
        roughness: 0.44,
        metalness: 0.04,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
        emissive: selected ? new THREE.Color(body.color) : new THREE.Color(0x000000),
        emissiveIntensity: selected ? 0.24 : 0
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.nodeId = body.nodeId;
      mesh.userData.selected = selected;
      group.add(mesh);

      if (showEdges) {
        const lineGeometry = new THREE.BufferGeometry();
        lineGeometry.setAttribute("position", new THREE.BufferAttribute(body.lines, 3));
        const lines = new THREE.LineSegments(
          lineGeometry,
          new THREE.LineBasicMaterial({ color: selected ? 0xffffff : 0x20252c, transparent: true, opacity: selected ? 0.95 : 0.72 })
        );
        lines.userData.nodeId = body.nodeId;
        group.add(lines);
      }
      runtime.bodyRoot.add(group);
    }
    const selectedId = selectedNodeIds.length === 1 ? selectedNodeIds[0] : undefined;
    const selectedGroup = runtime.bodyRoot.children.find((child) => child.userData.nodeId === selectedId);
    runtime.selectedGroup = selectedGroup instanceof THREE.Group ? selectedGroup : null;
    if (runtime.selectedGroup && transformMode !== "select") {
      runtime.transform.setMode(transformMode);
      runtime.transform.attach(runtime.selectedGroup);
    }
    runtime.invalidate();
  }, [evaluation, selectedNodeIds, showEdges, transformMode]);

  return (
    <div className="viewport-shell" ref={containerRef} aria-label="3D modeling viewport">
      {!evaluation && <div className="viewport-empty">Create a primitive to begin</div>}
      <div className="viewport-hint">Orbit: left drag · Pan: middle/right drag · Zoom: wheel</div>
    </div>
  );
}
